'use client';

import { useState, useEffect, useRef, useCallback, useId } from 'react';
import { createClient } from '@/lib/supabase/client';
import type { AchievementEvent } from '@/lib/training/types';

export interface UseAchievementNotificationsReturn {
  latestEvent: AchievementEvent | null;
  events: AchievementEvent[];
  clearLatest: () => void;
}

export function useAchievementNotifications(userId: string | null): UseAchievementNotificationsReturn {
  const [events, setEvents] = useState<AchievementEvent[]>([]);
  const [latestEvent, setLatestEvent] = useState<AchievementEvent | null>(null);
  const channelRef = useRef<ReturnType<ReturnType<typeof createClient>['channel']> | null>(null);
  const instanceId = useId();

  useEffect(() => {
    if (!userId) return;

    const supabase = createClient();
    const channel = supabase
      .channel(`achievement-events-${instanceId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'achievement_events',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const event = payload.new as AchievementEvent;
          if (event.event_type === 'achievement_unlocked') {
            setLatestEvent(event);
            setEvents(prev => [event, ...prev].slice(0, 50));
          }
        },
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [userId]);

  const clearLatest = useCallback(() => setLatestEvent(null), []);

  return { latestEvent, events, clearLatest };
}
